import React, { useState, useEffect } from "react";
import { Sparkles, CalendarCheck, Send, Loader2, UserCheck, Phone, Building2, BellRing, CheckCheck } from "lucide-react";
import { scheduleFromMail, sendMail, confirmFromMail } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { Badge, INTENTS, inputCls, fmtSlot } from "./mailUtils";

const MailAgentCard = ({ mail, availability, onDone }) => {
  const ai = mail.ai;
  const [slot, setSlot] = useState(ai.chosen_slot || "");
  const [major, setMajor] = useState(ai.major_login || "");
  const [subject, setSubject] = useState(ai.reply_subject || `Re: ${mail.subject}`);
  const [body, setBody] = useState(ai.reply_body || "");
  const [busy, setBusy] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    setSlot(ai.chosen_slot || ""); setMajor(ai.major_login || "");
    setSubject(ai.reply_subject || `Re: ${mail.subject}`); setBody(ai.reply_body || "");
  }, [ai, mail.subject]);

  const majors = availability?.majors || [];
  const freeAt = (login) => !slot || (availability?.slots || []).some((s) => s.date === slot && s.free_majors.includes(login));
  const done = mail.agent_status === "rdv_planifie";
  const confirmed = mail.agent_status === "rdv_confirme";
  const isConfirmation = ai.intent === "confirmation";

  const confirm = async () => {
    setBusy("confirm");
    try {
      const r = await confirmFromMail(mail.id);
      toast({ title: "RDV confirmé ✓", description: r.data.notified ? `Major prévenu : ${r.data.notified}` : "Aucun email de major (admin prévenu)." });
      onDone?.();
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Confirmation impossible", variant: "destructive" });
    } finally { setBusy(null); }
  };

  const schedule = async () => {
    if (!slot) return toast({ title: "Choisissez un créneau", variant: "destructive" });
    setBusy("schedule");
    try {
      const res = await scheduleFromMail(mail.id, { date: slot, major_login: major, send_reply: !!body, reply_subject: subject, reply_body: body });
      const a = res.data.appointment;
      toast({
        title: "RDV planifié ✓",
        description: `${fmtSlot(a.date)}${a.major_nom ? ` · ${a.major_nom}` : ""}${a.dolibarr_event_id ? " · Dolibarr ✓" : a.dolibarr_error ? " · Dolibarr indisponible" : ""}${res.data.sent && !res.data.sent.ok ? " · réponse NON envoyée" : ""}`,
      });
      onDone?.();
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec de la planification", variant: "destructive" });
    } finally { setBusy(null); }
  };

  const replyOnly = async () => {
    setBusy("reply");
    try {
      await sendMail({ to: mail.from_email, subject, body, account: mail.account || "prospecting", reply_to_id: mail.id });
      toast({ title: "Réponse envoyée ✓" });
      onDone?.();
    } catch (err) {
      toast({ title: "Échec de l'envoi", description: err?.response?.data?.detail || "Erreur SMTP", variant: "destructive" });
    } finally { setBusy(null); }
  };

  return (
    <div className="border border-violet-200 bg-violet-50/40 rounded-xl p-4 space-y-3" data-testid="mail-agent-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles size={16} className="text-violet-600" />
        <span className="font-semibold text-neutral-900 text-sm">Analyse de l'agent IA</span>
        <Badge map={INTENTS} value={ai.intent} testid="mail-ai-intent" />
        {ai.urgency === "haute" && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Urgent</span>}
      </div>
      <p className="text-sm text-neutral-700" data-testid="mail-ai-summary">{ai.summary}</p>
      <div className="flex flex-wrap gap-4 text-xs text-neutral-600">
        {ai.company && <span className="flex items-center gap-1"><Building2 size={13} /> {ai.company}</span>}
        {ai.contact_name && <span className="flex items-center gap-1"><UserCheck size={13} /> {ai.contact_name}</span>}
        {ai.phone && <span className="flex items-center gap-1"><Phone size={13} /> {ai.phone}</span>}
      </div>

      {mail.alert_sent_at && (
        <p className="text-xs text-orange-700 flex items-center gap-1.5" data-testid="mail-ai-alerted"><BellRing size={13} /> Alerte envoyée au super-admin ({mail.alert_reason}).</p>
      )}
      {isConfirmation && (
        confirmed ? (
          <p className="text-sm text-emerald-700 font-semibold flex items-center gap-1.5" data-testid="mail-ai-confirmed"><CheckCheck size={15} /> Rendez-vous confirmé · le major a été prévenu par email.</p>
        ) : (
          <button onClick={confirm} disabled={!!busy} data-testid="mail-ai-confirm-btn"
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
            {busy === "confirm" ? <Loader2 size={15} className="animate-spin" /> : <CheckCheck size={15} />} Confirmer le RDV & prévenir le major
          </button>
        )
      )}

      {ai.rdv_wanted && !done && !confirmed && (
        <div className="grid sm:grid-cols-2 gap-3 bg-white rounded-lg p-3 border border-neutral-200">
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1">Créneau proposé</label>
            <input type="datetime-local" className={inputCls} value={slot} data-testid="mail-ai-slot" onChange={(e) => setSlot(e.target.value)} />
            {ai.preferred_datetimes?.length > 0 && <p className="text-[11px] text-neutral-500 mt-1">Souhait du prospect : {ai.preferred_datetimes.map(fmtSlot).join(", ")}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1">Major en charge</label>
            <select className={inputCls} value={major} data-testid="mail-ai-major" onChange={(e) => setMajor(e.target.value)}>
              <option value="">{majors.length ? "— Non affecté —" : "Aucun major configuré (Utilisateurs)"}</option>
              {majors.map((m) => (
                <option key={m.login} value={m.login}>{m.nom}{freeAt(m.login) ? " · disponible" : " · occupé"}</option>
              ))}
            </select>
          </div>
        </div>
      )}
      {done && <p className="text-sm text-emerald-700 font-semibold flex items-center gap-1.5" data-testid="mail-ai-done"><CalendarCheck size={15} /> Rendez-vous planifié dans l'agenda.</p>}

      {!done && ai.intent !== "spam" && !(isConfirmation && confirmed && mail.replied) && (
        <div className="space-y-2">
          <input className={inputCls} value={subject} data-testid="mail-ai-reply-subject" onChange={(e) => setSubject(e.target.value)} />
          <textarea rows={7} className={inputCls} value={body} data-testid="mail-ai-reply-body" onChange={(e) => setBody(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            {ai.rdv_wanted && (
              <button onClick={schedule} disabled={!!busy} data-testid="mail-ai-schedule-btn"
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
                {busy === "schedule" ? <Loader2 size={15} className="animate-spin" /> : <CalendarCheck size={15} />} Planifier le RDV {body ? "& envoyer la réponse" : ""}
              </button>
            )}
            <button onClick={replyOnly} disabled={!!busy || !body} data-testid="mail-ai-reply-btn"
              className="flex items-center gap-2 border border-neutral-300 hover:border-brand text-neutral-800 text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
              {busy === "reply" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Envoyer la réponse seule
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailAgentCard;
